import { Decimal } from 'decimal.js';
import { BASE_CURRENCY, PERCENT_DP } from './constants';
import { CurrencyMismatchError, InvalidPositionError } from './errors';
import { tryResolveFxRate } from './fx';
import { HoldingValidationError } from './holdingInput';
import { fromDecimal, isDecimalString, roundTo, toDecimal } from './money';
import { reducePosition, type Position } from './position';
import type { AssetType, CurrencyCode, FxRate, Holding, PortfolioTotals } from './types';

/**
 * ผลตอบแทนที่รับรู้แล้ว — กำไรจากการขาย + ปันผล (spec Section 4.8 / 5.5)
 *
 * ## ทำไมต้องมีที่เก็บแยก
 *
 * ระบบเป็น position-based (spec Section 1) ขายหมดแล้ว holding ถูกลบทิ้ง กำไรก้อนนั้น
 * จึงหายไปจากระบบ ทั้งที่มันคือเงินที่ทำได้จริง — คำถาม "ตั้งแต่เริ่มลงทุนมา กำไรจริงไหม"
 * ตอบไม่ได้ถ้าไม่มีที่จำไว้
 *
 * ## ทำไมไม่ใช่ transaction ledger
 *
 * เก็บเฉพาะ **การขาย** กับ **ปันผล** — ไม่เก็บการซื้อ ไม่ย้อนสร้างสถานะพอร์ตจากประวัติ
 * holding ยังเป็นแหล่งความจริงของ "ตอนนี้ถืออะไรอยู่" เหมือนเดิม รายการพวกนี้ลบทิ้งได้
 * โดยพอร์ตไม่กระทบ และไม่มีสูตรไหนต้องไล่ประวัติจากต้นเพื่อหาต้นทุน
 *
 * ## เก็บอะไร ไม่เก็บอะไร
 *
 * เก็บเฉพาะข้อเท็จจริง ณ ตอนนั้น (จำนวน, ราคาขาย, ต้นทุนเฉลี่ยตอนขาย, ยอดปันผล)
 * **ไม่เก็บกำไร** — คำนวณสดทุกครั้งตามกฎเดียวกับ holding (spec Section 4.3)
 * ต้นทุนเฉลี่ยตอนขายต้องเก็บ เพราะมันไม่ใช่ค่าที่คำนวณได้อีกแล้วหลัง holding ถูกแก้หรือลบ
 */

/** ข้อมูลของสินทรัพย์ที่คัดลอกมาตอนบันทึก — holding ต้นทางอาจถูกลบไปแล้ว */
interface RealizedBase {
  platform: string;
  assetType: AssetType;
  symbol: string;
  displayName: string;
  currency: CurrencyCode;
}

/** การขายหนึ่งครั้ง — ทุกตัวเลขอยู่ในสกุล `currency` ซึ่งล็อกตาม holding ตอนขาย */
export interface ValidatedSale extends RealizedBase {
  kind: 'sale';
  /** decimal string — จำนวนที่ขาย */
  quantity: string;
  /** decimal string — ต้นทุนเฉลี่ยต่อหน่วย ณ ตอนขาย */
  avgCost: string;
  /** decimal string — ราคาขายต่อหน่วย */
  price: string;
}

/** ปันผลที่ได้รับหนึ่งครั้ง */
export interface ValidatedDividend extends RealizedBase {
  kind: 'dividend';
  /** decimal string — ยอดที่ได้รับจริง ในสกุล `currency` */
  amount: string;
  note: string | null;
}

/** ผ่านการตรวจแล้ว พร้อมเขียนลง Firestore (ยังไม่มี id/timestamp) */
export type ValidatedRealized = ValidatedSale | ValidatedDividend;

/** รายการที่อ่านกลับมาจาก Firestore */
export type RealizedEntry = ValidatedRealized & {
  id: string;
  /** UTC — null เมื่อ server ยังไม่ได้ประทับเวลา */
  recordedAt: Date | null;
};

function baseOf(holding: Holding): RealizedBase {
  return {
    platform: holding.platform,
    assetType: holding.assetType,
    symbol: holding.symbol,
    displayName: holding.displayName,
    currency: holding.currency,
  };
}

// ---------------------------------------------------------------------------
// การขาย
// ---------------------------------------------------------------------------

export interface SaleInput {
  /** decimal string — จำนวนที่ขาย */
  quantity: string;
  /** decimal string — ราคาขายต่อหน่วย */
  price: string;
  currency: CurrencyCode;
}

/** ผลของการขาย: สถานะใหม่ของ holding + รายการขายที่ต้องบันทึกคู่กัน */
export interface SalePlan {
  /** `null` = ขายหมดพอดี ผู้เรียกต้องลบ holding ทิ้ง (spec Section 5.4) */
  next: Position | null;
  sale: ValidatedSale;
}

/**
 * ขาย = ลดสถานะ (spec Section 5.4) + จดการขายไว้คำนวณกำไรที่รับรู้ (spec Section 5.5)
 *
 * ราคาขายต้องอยู่ในสกุลเดียวกับต้นทุนเดิม ถ้าไม่ตรงให้ throw — กติกาเดียวกับการซื้อเพิ่ม
 * (spec Section 5.3) เพราะ "ราคาขาย − ต้นทุน" ข้ามสกุลเงินไม่มีความหมาย
 */
export function planSale(holding: Holding, input: SaleInput): SalePlan {
  // ต้นทุนต่อหน่วยของเงินสดเป็น 1 เสมอ ขายเงินสดจึงไม่มีกำไรให้รับรู้
  if (holding.assetType === 'cash') {
    throw new InvalidPositionError('เงินสดไม่มีการขาย ให้แก้จำนวนเงินโดยตรง');
  }
  if (holding.currency.toUpperCase() !== input.currency.toUpperCase()) {
    throw new CurrencyMismatchError(holding.currency, input.currency);
  }

  const current: Position = {
    quantity: holding.quantity,
    avgCost: holding.avgCost,
    currency: holding.currency,
  };
  const next = reducePosition(current, input.quantity);

  const price = toDecimal(input.price, 'sale.price');
  if (price.isNegative()) {
    throw new InvalidPositionError('ราคาที่ขายต้องไม่ติดลบ');
  }

  return {
    next,
    sale: {
      kind: 'sale',
      ...baseOf(holding),
      quantity: fromDecimal(toDecimal(input.quantity, 'sale.quantity')),
      avgCost: holding.avgCost,
      price: fromDecimal(price),
    },
  };
}

/** กำไรที่รับรู้ของการขายหนึ่งครั้ง ในสกุลของรายการ */
export interface SalePnl {
  /** decimal string */
  pnl: string;
  /** null เมื่อต้นทุนเป็นศูนย์ — กติกาเดียวกับ spec Section 5.2 */
  pnlPct: string | null;
}

/**
 * spec Section 5.5
 *
 *   realizedPnl = quantity × (price − avgCost)
 *   realizedPct = (price − avgCost) / avgCost × 100
 */
export function salePnl(sale: Pick<ValidatedSale, 'quantity' | 'avgCost' | 'price'>): SalePnl {
  const quantity = toDecimal(sale.quantity, 'sale.quantity');
  const avgCost = toDecimal(sale.avgCost, 'sale.avgCost');
  const price = toDecimal(sale.price, 'sale.price');

  const perUnit = price.minus(avgCost);

  return {
    pnl: fromDecimal(quantity.mul(perUnit)),
    pnlPct: avgCost.isZero() ? null : roundTo(perUnit.div(avgCost).mul(100), PERCENT_DP),
  };
}

// ---------------------------------------------------------------------------
// ปันผล
// ---------------------------------------------------------------------------

export interface DividendDraft {
  /** decimal string — ยอดที่ได้รับจริง */
  amount: string;
  currency: string;
  note?: string | null;
}

const CURRENCY_PATTERN = /^[A-Z]{3}$/;
const MAX_NOTE_LENGTH = 500;

/**
 * ตรวจปันผลที่ผู้ใช้กรอก — throw `HoldingValidationError` พร้อมชื่อ field ที่ผิด
 *
 * สกุลเงิน **ไม่ล็อก** ตาม holding ต่างจากการขาย เพราะปันผลไม่ได้ถูกลบกับต้นทุน
 * มันเป็นเงินก้อนใหม่ที่แปลงเป็นบาทได้เองตรง ๆ และโบรกบางเจ้าจ่ายคนละสกุลกับที่ซื้อจริง
 */
export function validateDividendDraft(holding: Holding, draft: DividendDraft): ValidatedDividend {
  if (holding.assetType === 'cash') {
    throw new HoldingValidationError('holding', 'เงินสดไม่มีปันผล');
  }

  const rawAmount = draft.amount.trim();
  if (rawAmount.length === 0) {
    throw new HoldingValidationError('amount', 'กรุณากรอกยอดปันผล');
  }
  if (!isDecimalString(rawAmount)) {
    throw new HoldingValidationError('amount', 'ยอดปันผลต้องเป็นตัวเลข ไม่ใส่ comma');
  }
  const amount = toDecimal(rawAmount, 'amount');
  if (amount.lessThanOrEqualTo(0)) {
    throw new HoldingValidationError('amount', 'ยอดปันผลต้องมากกว่า 0');
  }

  const currency = draft.currency.trim().toUpperCase();
  if (!CURRENCY_PATTERN.test(currency)) {
    throw new HoldingValidationError('currency', 'สกุลเงินต้องเป็นรหัส 3 ตัวอักษร เช่น USD');
  }

  const note = (draft.note ?? '').trim();
  if (note.length > MAX_NOTE_LENGTH) {
    throw new HoldingValidationError('note', `โน้ตยาวเกิน ${MAX_NOTE_LENGTH} ตัวอักษร`);
  }

  return {
    kind: 'dividend',
    ...baseOf(holding),
    currency,
    amount: fromDecimal(amount),
    note: note.length === 0 ? null : note,
  };
}

// ---------------------------------------------------------------------------
// ตีมูลค่าและรวมยอด
// ---------------------------------------------------------------------------

export interface RealizedValuation {
  entryId: string;
  /** กำไรของการขาย หรือยอดปันผล — ในสกุลของรายการ คำนวณได้เสมอ */
  amount: string;
  /** เป็นบาท — null เมื่อหา FX ไม่เจอ ห้ามแสดงเป็น 0 (spec Section 9.3) */
  amountTHB: string | null;
  /** เฉพาะการขาย — null สำหรับปันผล หรือเมื่อต้นทุนเป็นศูนย์ */
  pnlPct: string | null;
}

/**
 * แปลงรายการที่รับรู้แล้วเป็นบาท
 *
 * ⚠️ ใช้อัตราแลกเปลี่ยน **ปัจจุบัน** ไม่ใช่ ณ วันที่ขาย — ตรงกับที่ต้นทุนของ holding
 * ก็แปลงด้วยอัตราปัจจุบันเหมือนกัน (spec Section 5.2) ตัวเลขทั้งหน้าจึงอยู่บนฐานเดียวกัน
 * ข้อจำกัดนี้บันทึกไว้ที่ spec Section 13
 */
export function valuateRealized(
  entry: RealizedEntry,
  rates: ReadonlyMap<string, FxRate>,
): RealizedValuation {
  let amount: string;
  let pnlPct: string | null = null;

  if (entry.kind === 'sale') {
    const pnl = salePnl(entry);
    amount = pnl.pnl;
    pnlPct = pnl.pnlPct;
  } else {
    amount = fromDecimal(toDecimal(entry.amount, 'dividend.amount'));
  }

  const rate = tryResolveFxRate(entry.currency, BASE_CURRENCY, rates);

  return {
    entryId: entry.id,
    amount,
    amountTHB: rate === null ? null : fromDecimal(toDecimal(amount).mul(rate)),
    pnlPct,
  };
}

export interface RealizedTotals {
  /** decimal string — กำไรสุทธิจากการขายทั้งหมด (ติดลบได้) */
  salePnlTHB: string;
  /** decimal string — ปันผลสะสม */
  dividendsTHB: string;
  /** decimal string — สองก้อนข้างบนรวมกัน */
  totalTHB: string;
  saleCount: number;
  dividendCount: number;
  /** จำนวนรายการที่แปลงเป็นบาทไม่ได้ จึงไม่ถูกนับรวม */
  incompleteCount: number;
  isPartial: boolean;
}

/** รวมยอดที่รับรู้แล้ว — รายการที่หา FX ไม่เจอไม่ถูกนับ แต่ถูกนับใน `incompleteCount` */
export function summarizeRealized(
  entries: readonly RealizedEntry[],
  rates: ReadonlyMap<string, FxRate>,
): RealizedTotals {
  let sales = new Decimal(0);
  let dividends = new Decimal(0);
  let saleCount = 0;
  let dividendCount = 0;
  let incompleteCount = 0;

  for (const entry of entries) {
    if (entry.kind === 'sale') {
      saleCount += 1;
    } else {
      dividendCount += 1;
    }

    const { amountTHB } = valuateRealized(entry, rates);
    if (amountTHB === null) {
      incompleteCount += 1;
      continue;
    }

    if (entry.kind === 'sale') {
      sales = sales.plus(toDecimal(amountTHB));
    } else {
      dividends = dividends.plus(toDecimal(amountTHB));
    }
  }

  return {
    salePnlTHB: fromDecimal(sales),
    dividendsTHB: fromDecimal(dividends),
    totalTHB: fromDecimal(sales.plus(dividends)),
    saleCount,
    dividendCount,
    incompleteCount,
    isPartial: incompleteCount > 0,
  };
}

export interface TotalReturn {
  /** decimal string — กำไร/ขาดทุนของที่ถืออยู่ + ที่ขายไปแล้ว + ปันผล */
  totalReturnTHB: string;
  /** ฝั่งใดฝั่งหนึ่งนับไม่ครบ → ตัวเลขนี้ยังไม่ครบ */
  isPartial: boolean;
}

/**
 * spec Section 5.5 — ผลตอบแทนรวมตั้งแต่เริ่มลงทุน
 *
 *   totalReturnTHB = totalPnlTHB + realizedSalePnlTHB + dividendsTHB
 *
 * ไม่มีเปอร์เซ็นต์ — ตัวหารที่ถูกต้องคือเงินทุนที่ใส่เข้าไปจริงตลอดช่วงเวลา ซึ่งต้องมี
 * ประวัติการซื้อทั้งหมด (XIRR อยู่นอก scope) ตัวหารแบบอื่นให้เลขที่ดูน่าเชื่อแต่ผิด
 */
export function totalReturn(portfolio: PortfolioTotals, realized: RealizedTotals): TotalReturn {
  return {
    totalReturnTHB: fromDecimal(
      toDecimal(portfolio.pnlTHB, 'portfolio.pnlTHB').plus(
        toDecimal(realized.totalTHB, 'realized.totalTHB'),
      ),
    ),
    isPartial: portfolio.isPartial || realized.isPartial,
  };
}
